import React from "react";
import PageHero from "../components/PageHero";
import VisaServicesSection from "../components/VisaServicesSection";
import ConsultationCTASection from "../components/ConsultationCTASection";
import StatsStrip from "../components/StatsStrip";
import FAQSection from "../components/FAQSection";
import { motion } from "framer-motion";
import {
  Check,
  Crown,
  Briefcase,
  Heart,
  GraduationCap,
  Flame,
  Medal,
} from "lucide-react";

const GOLDEN = [
  "10-year UAE Golden Visa for eligible investors & entrepreneurs",
  "Self-sponsored residency — no employer dependency",
  "Sponsor immediate family, parents and household staff",
  "Priority airport, banking and government services",
  "Fast-track processing via senior StartINC liaisons",
  "Renewal, status-change and compliance handled end-to-end",
];

const VISA_TYPES = [
  {
    icon: Crown,
    title: "Golden Visa (10 years)",
    who: "Investors · Specialised talent · Entrepreneurs",
    copy: "Long-term residency with multiple-entry, self-sponsorship and family coverage.",
  },
  {
    icon: Briefcase,
    title: "Investor / Partner Visa (2 years)",
    who: "Shareholders in UAE entities",
    copy: "Standard renewable residency for founders and shareholders of UAE companies.",
  },
  {
    icon: Flame,
    title: "Green Visa (5 years)",
    who: "Freelancers · Self-employed · Skilled employees",
    copy: "Self-sponsored 5-year residency with expanded family sponsorship rights.",
  },
  {
    icon: Heart,
    title: "Family Residence Visa",
    who: "Spouse · Children · Parents · Staff",
    copy: "Bring your loved ones to the UAE — attestation, medical and Emirates ID included.",
  },
  {
    icon: GraduationCap,
    title: "Student & Virtual Work",
    who: "Students & remote professionals",
    copy: "Virtual working program and student residence for the next generation of talent.",
  },
  {
    icon: Medal,
    title: "Talent & Specialised Visa",
    who: "Scientists · Doctors · Athletes · Creators",
    copy: "Federal and emirate-level talent pathways for outstanding professionals.",
  },
];

const ELIGIBILITY = [
  { tag: "Investor", text: "Real estate investment ≥ AED 2M, or public investment ≥ AED 2M." },
  { tag: "Entrepreneur", text: "Founder of a project valued ≥ AED 500K or backed by an accredited business incubator." },
  { tag: "Specialised Talent", text: "Doctors, scientists, PhDs, creatives, athletes — with relevant documentation." },
  { tag: "Executive", text: "Senior managers with monthly salary ≥ AED 30K and a UAE employment contract." },
  { tag: "Outstanding Student", text: "Top-ranked UAE or international university graduates." },
  { tag: "Humanitarian / Pioneer", text: "Frontline professionals and UAE national-interest contributors." },
];

export default function VisaServicesPage() {
  return (
    <div data-testid="visa-page">
      <PageHero
        eyebrow="Visa & Residency"
        title="Your gateway to"
        highlight="UAE residency."
        copy="From investor and Golden Visas to employee work permits and family sponsorship — we handle the full immigration lifecycle so you can focus on building."
        image="https://images.pexels.com/photos/7793999/pexels-photo-7793999.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
      />

      <StatsStrip />
      <VisaServicesSection />

      {/* Visa Types Grid */}
      <section className="bg-[#F8FAFC] py-20 md:py-28">
        <div className="container-x">
          <div className="max-w-2xl">
            <p className="overline text-[#3366FF]">Residency pathways</p>
            <div className="gold-rule mt-5 mb-6" />
            <h2 className="font-heading font-light tracking-tight text-[#0F172A] text-3xl md:text-4xl lg:text-5xl leading-tight">
              Six UAE residency pathways —{" "}
              <span className="font-black text-[#002366]">one of them is yours</span>.
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {VISA_TYPES.map(({ icon: Icon, title, who, copy }, i) => (
              <motion.div
                key={title}
                data-testid={`visa-type-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: (i % 3) * 0.08 }}
                className="rounded-sm border border-slate-200 bg-white p-7 hover:shadow-xl hover:border-[#3366FF]/40 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-sm bg-[#EEF2FF] text-[#3366FF] inline-flex items-center justify-center">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <span className="font-heading font-black text-3xl text-slate-100 group-hover:text-[#EEF2FF] transition-colors">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="mt-5 font-heading font-semibold text-lg tracking-tight text-[#0F172A]">
                  {title}
                </h3>
                <p className="mt-1 text-xs font-heading font-bold tracking-widest uppercase text-[#3366FF]">
                  {who}
                </p>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{copy}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Golden Visa */}
      <section className="bg-white py-20 md:py-28">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <p className="overline text-[#3366FF]">The Golden Visa</p>
            <div className="gold-rule mt-5 mb-6" />
            <h2 className="font-heading font-light tracking-tight text-3xl md:text-4xl text-[#0F172A] leading-tight">
              10 years of residency.{" "}
              <span className="font-black text-[#002366]">Unconditional</span> mobility.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              The UAE Golden Visa is a long-term residence permit for investors,
              entrepreneurs, specialised talent and outstanding students. StartINC
              identifies your most efficient pathway and prepares every document to a
              government-grade standard.
            </p>
          </div>
          <div className="lg:col-span-6">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {GOLDEN.map((t) => (
                <li key={t} className="flex gap-3 text-slate-700">
                  <Check className="w-5 h-5 shrink-0 text-[#3366FF] mt-0.5" />
                  <span className="text-sm md:text-base">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Eligibility */}
      <section className="bg-[#0A1128] text-white py-20 md:py-28">
        <div className="container-x">
          <div className="max-w-2xl">
            <p className="overline text-[#6B8AFF]">Golden Visa eligibility</p>
            <div className="gold-rule mt-5 mb-6" />
            <h2 className="font-heading font-light tracking-tight text-3xl md:text-4xl leading-tight">
              Six headline routes to{" "}
              <span className="font-black text-[#6B8AFF]">10-year residency</span>.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {ELIGIBILITY.map(({ tag, text }, i) => (
              <div
                key={tag}
                data-testid={`eligibility-${i}`}
                className="card-dark rounded-sm p-6"
              >
                <span className="chip-dark">{tag}</span>
                <p className="mt-4 text-slate-200/90 leading-relaxed text-sm md:text-base">
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FAQSection />

      <ConsultationCTASection
        eyebrow="Start your residency"
        title="Secure long-term UAE residency for you and your family."
        service="visa"
      />
    </div>
  );
}
